import { useTranslation } from 'react-i18next';
import { useState, useCallback, useMemo } from 'react';
import { Box, Center, Text } from '@chakra-ui/react';
import {
  DataTable,
  StatusBadge,
  type Column,
  type StatusType,
} from '@themes/aegix';
import type { EventSite } from '../../../types/ReunificationTypes';
import { LocationDetailDrawer } from './LocationDetailDrawer';

interface LocationRow {
  [key: string]: unknown;
  id: string;
  name: string;
  type: string;
  role: string;
  status: StatusType;
  activated: string;
}

function transformSites(sites: EventSite[]): LocationRow[] {
  return sites.map((s) => ({
    id: s.public_id,
    name: s.site_name,
    type: s.site_type,
    role: s.role_display,
    status: s.is_active ? 'active' : 'inactive',
    activated: s.activated_at
      ? new Date(s.activated_at).toLocaleString()
      : '---',
  }));
}

interface EventLocationsPanelProps {
  eventId: string;
  sites: EventSite[];
}

export function EventLocationsPanel({ sites }: EventLocationsPanelProps) {
  const { t } = useTranslation();
  const [selectedSite, setSelectedSite] = useState<EventSite>();

  const columns: Column<LocationRow>[] = useMemo(() => [
    { key: 'name', label: t('sites.siteName', 'Site Name'), sortable: true, width: '200px' },
    { key: 'type', label: t('common.type', 'Type'), sortable: true, width: '140px' },
    { key: 'role', label: t('common.role', 'Role'), sortable: true, width: '140px' },
    {
      key: 'status',
      label: t('common.status', 'Status'),
      sortable: false,
      width: '100px',
      render: (row) => <StatusBadge status={row.status} label={t(`common.${row.status}`, row.status)} />,
    },
    { key: 'activated', label: t('events.col.activated', 'Activated'), sortable: true, width: '160px' },
  ], [t]);

  const rows = transformSites(sites);

  const handleRowClick = useCallback(
    (row: LocationRow) => {
      const site = sites.find((s) => s.public_id === row.id);
      if (site) setSelectedSite(site);
    },
    [sites],
  );

  if (sites.length === 0) {
    return (
      <Center py={12}>
        <Text color="text.secondary">{t('events.noLocationsAssigned', 'No locations assigned to this event')}</Text>
      </Center>
    );
  }

  return (
    <>
      <Box pb={3}>
        <DataTable
          columns={columns}
          data={rows}
          keyExtractor={(row) => row.id}
          onRowClick={handleRowClick}
        />
      </Box>

      <LocationDetailDrawer
        isOpen={!!selectedSite}
        onClose={() => setSelectedSite(undefined)}
        site={selectedSite}
      />
    </>
  );
}
